// @ts-nocheck
import { geocodeWithFallback } from "@kawkaw/shared";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, Checkbox, Form, Input, InputNumber, Select, Slider, Switch, TimePicker, message,
} from "../components/admin-ui";
import {
  BankOutlined, BellOutlined, CarOutlined, ClockCircleOutlined,
  CloseCircleOutlined, CreditCardOutlined, DollarOutlined,
  EnvironmentOutlined, InfoCircleOutlined, LockOutlined,
  MailOutlined, MessageOutlined, MobileOutlined, PercentageOutlined, PhoneOutlined, PictureOutlined, PrinterOutlined,
  QrcodeOutlined, ShoppingOutlined, SoundOutlined, TabletOutlined,
  TeamOutlined, UserOutlined, WalletOutlined,
} from "../components/admin-icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { HTTPError } from "ky";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
const { color: T } = tokens;
import { useAdminStalls, useAdminStaff, useAdminSettings, geocodeViaGoogle, useGoogleMapsKey, useSetGoogleMapsKey, useUpdateSettings, useUpdateLogo, useKopitiamHours, useUpdateHours } from "../api/hooks";
import { ImageUpload } from "../components/ImageUpload";
import { jsPDF } from "jspdf";

/**
 * "Test Print" against a real thermal printer isn't wired yet (would need a
 * local print-server bridge, out of scope here) — until then, this generates
 * a real PDF sized to the configured paper width so the store can hand it to
 * different kitchen printers to check which one actually fits/reads it,
 * rather than the button silently doing nothing.
 */
function handleTestPrint({ printerName, connType, printerIp, paperWidth }: {
  printerName: string; connType: string; printerIp: string; paperWidth: "58mm" | "80mm";
}) {
  const widthMm = paperWidth === "58mm" ? 58 : 80;
  const doc = new jsPDF({ unit: "mm", format: [widthMm, 150] });
  const cx = widthMm / 2;
  let y = 10;
  doc.setFontSize(11).setFont("courier", "bold");
  doc.text("TEST PRINT", cx, y, { align: "center" }); y += 6;
  doc.setFontSize(8).setFont("courier", "normal");
  doc.text(new Date().toLocaleString(), cx, y, { align: "center" }); y += 6;
  doc.text("-".repeat(paperWidth === "58mm" ? 32 : 42), cx, y, { align: "center" }); y += 5;
  const line = (label: string, value: string) => { doc.text(`${label}: ${value || "—"}`, 4, y); y += 5; };
  line("Printer name", printerName);
  line("Connection", connType);
  line("IP / address", printerIp);
  line("Paper width", paperWidth);
  y += 2;
  doc.text("-".repeat(paperWidth === "58mm" ? 32 : 42), cx, y, { align: "center" }); y += 6;
  doc.setFontSize(7);
  doc.text("If this page's width matches your", cx, y, { align: "center" }); y += 4;
  doc.text("printer's paper roll and text is legible,", cx, y, { align: "center" }); y += 4;
  doc.text("this printer/paper-width combo works.", cx, y, { align: "center" });
  doc.save(`test-print-${paperWidth}-${Date.now()}.pdf`);
}

/* ── Operating hours ─────────────────────────────── */
const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"] as const;
type Day = (typeof DAYS)[number];
// Backend day_of_week follows JS Date.getDay() convention (0=Sunday..6=Saturday),
// while this screen's DAYS array is Monday-first for display purposes.
const DAY_TO_INDEX: Record<Day, number> = {
  monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0,
};
type DaySchedule = { enabled: boolean; from: Dayjs; to: Dayjs };
const DEFAULT_HOURS: Record<Day, DaySchedule> = {
  monday:    { enabled:true,  from:dayjs("08:00","HH:mm"), to:dayjs("22:00","HH:mm") },
  tuesday:   { enabled:true,  from:dayjs("08:00","HH:mm"), to:dayjs("22:00","HH:mm") },
  wednesday: { enabled:true,  from:dayjs("08:00","HH:mm"), to:dayjs("22:00","HH:mm") },
  thursday:  { enabled:true,  from:dayjs("08:00","HH:mm"), to:dayjs("22:00","HH:mm") },
  friday:    { enabled:true,  from:dayjs("08:00","HH:mm"), to:dayjs("23:30","HH:mm") },
  saturday:  { enabled:true,  from:dayjs("09:00","HH:mm"), to:dayjs("23:30","HH:mm") },
  sunday:    { enabled:false, from:dayjs("00:00","HH:mm"), to:dayjs("08:00","HH:mm") },
};

/* ── Helpers ─────────────────────────────────────── */
function SectionCard({ icon, title, extra, children }: {
  icon: React.ReactNode; title: string; extra?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <section className="admin-settings-section-card" style={{ background:T.surface, borderRadius:16, border:"1px solid #E5E7EB", padding:"20px 22px", marginBottom:20, boxShadow:"0 12px 28px rgba(15,23,42,0.04)" }}>
      <div className="admin-settings-section-heading" style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
        <div className="admin-settings-section-title" style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span className="admin-settings-section-icon" style={{ color:T.brand, fontSize:18 }}>{icon}</span>
          <h2 style={{ fontSize:16, fontWeight:800, color:T.textDark }}>{title}</h2>
        </div>
        {extra && <div className="admin-settings-section-extra">{extra}</div>}
      </div>
      <div className="admin-settings-section-body">{children}</div>
    </section>
  );
}
/* ── SMS templates ───────────────────────────────── */
// Mirrors DEFAULT_OTP_TEMPLATES in packages/backend/src/services/OtpService.ts
// exactly — shown as placeholder text so an admin sees what actually sends
// today even before typing a custom template, and "zh" being first/default
// matches customer-app's own default locale.
// Each starts with a price-disclosure prefix per Malaysia's SKMM guidelines
// ("Free msg/RM0/Msj ini percuma" at the start of every SMS) — confirmed
// required by real MY SMS gateways (sms123.net rejected unprefixed content
// with a whitelist error during integration testing).
const DEFAULT_SMS_TEMPLATES: Record<"zh"|"en"|"ms", string> = {
  zh: "RM0 您的 KawKaw 验证码是 {code}，{minutes} 分钟内有效。",
  en: "Free msg Your KawKaw verification code is {code}. It expires in {minutes} minutes.",
  ms: "Msj ini percuma Kod pengesahan KawKaw anda ialah {code}. Sah selama {minutes} minit.",
};
const renderSmsPreview = (template: string) => template.replace(/\{code\}/g, "482913").replace(/\{minutes\}/g, "5");

/* ── Tabs ────────────────────────────────────────── */
type TabKey = "store"|"delivery"|"tax"|"payment"|"printer"|"notifications"|"registration"|"sms"|"email";

/* ── Payment method config ───────────────────────── */
type PayKey = "cash"|"duitnow"|"fpx"|"card"|"grabpay"|"tng";
const PAY_NAME_KEYS: Record<PayKey, { name: string; desc: string }> = {
  cash:    { name: "payCashName",    desc: "payCashDesc" },
  duitnow: { name: "payDuitnowName", desc: "payDuitnowDesc" },
  fpx:     { name: "payFpxName",     desc: "payFpxDesc" },
  card:    { name: "payCardName",    desc: "payCardDesc" },
  grabpay: { name: "payGrabpayName", desc: "payGrabpayDesc" },
  tng:     { name: "payTngName",     desc: "payTngDesc" },
};
const PAY_ICONS: { key: PayKey; icon: React.ReactNode }[] = [
  { key: "cash",    icon: <DollarOutlined /> },
  { key: "duitnow", icon: <QrcodeOutlined /> },
  { key: "fpx",     icon: <BankOutlined /> },
  { key: "card",    icon: <CreditCardOutlined /> },
  { key: "grabpay", icon: <WalletOutlined /> },
  { key: "tng",     icon: <MobileOutlined /> },
];

/* ── Alert config ────────────────────────────────── */
type AlertKey = "newOrder"|"cancelled"|"riderAssigned"|"orderDelayed";
type AlertTag = { label: string; color: string; bg: string };
const ALERT_NAME_KEYS: Record<AlertKey, string> = {
  newOrder: "alertNewOrder",
  cancelled: "alertCancelled",
  riderAssigned: "alertRider",
  orderDelayed: "alertDelayed",
};
type AlertDef = { key: AlertKey; icon: React.ReactNode; iconBg: string; tagKinds: ("sound"|"push")[] };
const ALERT_DEFS: AlertDef[] = [
  { key: "newOrder", iconBg: T.brandLight, icon: <ShoppingOutlined style={{ color: T.brand }} />, tagKinds: ["sound", "push"] },
  { key: "cancelled", iconBg: "#FEF2F2", icon: <CloseCircleOutlined style={{ color: T.dangerText }} />, tagKinds: ["sound"] },
  { key: "riderAssigned", iconBg: T.surfaceBlue, icon: <TeamOutlined style={{ color: T.infoText }} />, tagKinds: ["push"] },
  { key: "orderDelayed", iconBg: T.bg, icon: <ClockCircleOutlined style={{ color: T.textMuted }} />, tagKinds: ["push"] },
];

/* ── Screen ──────────────────────────────────────── */
export function SettingsScreen() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("store");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  /* Store Info */
  // One real switch for delivery orders — the same delivery_control_enabled the POS Delivery Zone page toggles.
  const [allowDelivery, setAllowDelivery] = useState(true);
  const [hours, setHours] = useState<Record<Day, DaySchedule>>(DEFAULT_HOURS);

  /* Delivery — base fee (delivery_fee_default) and radius (delivery_radius_km)
     are the only two backend-real fields; the fee-preview below reflects the
     actual tiered formula PricingService.computeDeliveryFee uses. */
  const [baseFee, setBaseFee] = useState("2.00");
  const [radius,  setRadius]  = useState("10");

  /* Tax */
  const [chargeSst, setChargeSst] = useState(false);
  const [sstRate,   setSstRate]   = useState("8");

  /* Payment */
  const [payMethods, setPayMethods] = useState<Record<PayKey, boolean>>({
    cash:true, duitnow:true, fpx:true, card:true, grabpay:false, tng:false,
  });
  const [cashRefund, setCashRefund] = useState(true);
  const [bankRefund, setBankRefund] = useState(true);

  /* Printer */
  const [printerName, setPrinterName] = useState("");
  const [connType,    setConnType]    = useState("network");
  const [printerIp,   setPrinterIp]   = useState("192.168.1.15");
  const [paperWidth,  setPaperWidth]  = useState("80mm");
  const [autoPrintKitchen,  setAutoPrintKitchen]  = useState(true);
  const [autoPrintReceipt,  setAutoPrintReceipt]  = useState(true);
  const [manualTrigger,     setManualTrigger]     = useState(false);
  type KitchenKey = "orderId"|"orderType"|"customerName"|"itemsCustom"|"specialInstr"|"estTime";
  const [kitchenSlip, setKitchenSlip] = useState<Record<KitchenKey, boolean>>({
    orderId:true, orderType:true, customerName:true, itemsCustom:true, specialInstr:true, estTime:false,
  });
  type ReceiptKey = "storeAddr"|"orderDate"|"itemsPrices"|"subtotalEtc"|"totalAmount"|"payMethod";
  const [receiptContent, setReceiptContent] = useState<Record<ReceiptKey, boolean>>({
    storeAddr:true, orderDate:true, itemsPrices:true, subtotalEtc:true, totalAmount:true, payMethod:true,
  });

  /* Notifications */
  const [alerts, setAlerts] = useState<Record<AlertKey, boolean>>({
    newOrder:true, cancelled:true, riderAssigned:true, orderDelayed:false,
  });
  const [alertSoundOn,      setAlertSoundOn]      = useState(true);
  const [volume,            setVolume]            = useState(85);
  const [soundTheme,        setSoundTheme]        = useState("chime");
  const [tabletPushOn,      setTabletPushOn]      = useState(true);
  const [soundAlertOn,      setSoundAlertOn]      = useState(true);
  const [allStallNotifs,    setAllStallNotifs]    = useState(true);
  const [notifLanguage,     setNotifLanguage]     = useState("english");

/* ── SMS message templates ───────────────────────── */
const [smsDefaultLang, setSmsDefaultLang] = useState<"zh"|"en"|"ms">("zh");
const [smsTemplateZh,  setSmsTemplateZh]  = useState("");
const [smsTemplateEn,  setSmsTemplateEn]  = useState("");
const [smsTemplateMs,  setSmsTemplateMs]  = useState("");

/* ── OTP request limits + code shape ─────────────── */
const [otpDailyCap, setOtpDailyCap] = useState("5");
const [otpCooldownSeconds, setOtpCooldownSeconds] = useState("60");
const [otpExpirySeconds, setOtpExpirySeconds] = useState("300");
const [otpCodeLength, setOtpCodeLength] = useState("6");

/* ── Customer registration channels ──────────────── */
const [allowEmailRegistration, setAllowEmailRegistration] = useState(true);
const [allowPhoneRegistration, setAllowPhoneRegistration] = useState(true);

/* ── Email (Resend) OTP delivery ─────────────────── */
const [emailEnabled, setEmailEnabled] = useState(false);
const [emailProvider, setEmailProvider] = useState<"resend" | "smtp">("resend");
const [resendApiKey, setResendApiKey] = useState("");
const [emailFromAddress, setEmailFromAddress] = useState("");
const [smtpHost, setSmtpHost] = useState("");
const [smtpPort, setSmtpPort] = useState("587");
const [smtpSecure, setSmtpSecure] = useState(false);
const [smtpUser, setSmtpUser] = useState("");
const [smtpPass, setSmtpPass] = useState("");

/* ── Email OTP templates (per language) ──────────── */
type EmailLangTemplate = { subject: string; heading: string; intro: string; outro: string };
const EMPTY_EMAIL_TEMPLATE: EmailLangTemplate = { subject: "", heading: "", intro: "", outro: "" };
const [emailTemplateDefaultLang, setEmailTemplateDefaultLang] = useState<"zh" | "en" | "ms">("zh");
const [emailTemplateZh, setEmailTemplateZh] = useState<EmailLangTemplate>(EMPTY_EMAIL_TEMPLATE);
const [emailTemplateEn, setEmailTemplateEn] = useState<EmailLangTemplate>(EMPTY_EMAIL_TEMPLATE);
const [emailTemplateMs, setEmailTemplateMs] = useState<EmailLangTemplate>(EMPTY_EMAIL_TEMPLATE);

  const { t } = useTranslation();
  const printerDemoSeeded = useRef(false);
  useEffect(() => {
    if (printerDemoSeeded.current) return;
    setPrinterName(t("settingsScreen.printerNameDefault"));
    printerDemoSeeded.current = true;
  }, [t]);
  const fieldLabel = useCallback((id: string) => (
    <span className="admin-settings-field-label" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: T.textMuted }}>
      {t(`settingsScreen.${id}`)}
    </span>
  ), [t]);

  const tabs = useMemo(() => [
    { key: "store" as TabKey, label: t("settingsScreen.tabStore") },
    { key: "delivery" as TabKey, label: t("settingsScreen.tabDelivery") },
    { key: "tax" as TabKey, label: t("settingsScreen.tabTax") },
    { key: "payment" as TabKey, label: t("settingsScreen.tabPayment") },
    { key: "printer" as TabKey, label: t("settingsScreen.tabPrinter") },
    { key: "notifications" as TabKey, label: t("settingsScreen.tabNotifications") },
    { key: "sms" as TabKey, label: t("settingsScreen.tabSms") },
    { key: "registration" as TabKey, label: t("settingsScreen.tabRegistration") },
    { key: "email" as TabKey, label: t("settingsScreen.tabEmail") },
  ], [t]);

  const payMethodsList = useMemo(() => PAY_ICONS.map(({ key, icon }) => {
    const nk = PAY_NAME_KEYS[key];
    return {
      key,
      icon,
      name: t(`settingsScreen.${nk.name}` as const),
      desc: t(`settingsScreen.${nk.desc}` as const),
    };
  }), [t]);

  const alertItems = useMemo(() => ALERT_DEFS.map(d => ({
    key: d.key,
    icon: d.icon,
    iconBg: d.iconBg,
    name: t(`settingsScreen.${ALERT_NAME_KEYS[d.key]}` as const),
    tags: d.tagKinds.map(kind => (kind === "sound"
      ? { label: t("settingsScreen.tagSoundAlert"), color: T.brandAmberText, bg: T.warningBg }
      : { label: t("settingsScreen.tagTabletPush"), color: T.successText, bg: T.successBg })),
  })), [t]);

  const autoPrintRows = useMemo(() => [
    { key: "kitchen" as const, state: autoPrintKitchen, setState: setAutoPrintKitchen, name: t("settingsScreen.autoKitchenName"), desc: t("settingsScreen.autoKitchenDesc") },
    { key: "receipt" as const, state: autoPrintReceipt, setState: setAutoPrintReceipt, name: t("settingsScreen.autoReceiptName"), desc: t("settingsScreen.autoReceiptDesc") },
    { key: "manual" as const, state: manualTrigger, setState: setManualTrigger, name: t("settingsScreen.manualPrintName"), desc: t("settingsScreen.manualPrintDesc") },
  ], [t, autoPrintKitchen, autoPrintReceipt, manualTrigger]);

  const kitchenSlipRows = useMemo(() => ([
    { key: "orderId" as const, label: t("settingsScreen.kitchenOrderId") },
    { key: "orderType" as const, label: t("settingsScreen.kitchenOrderType") },
    { key: "customerName" as const, label: t("settingsScreen.kitchenCustomerName") },
    { key: "itemsCustom" as const, label: t("settingsScreen.kitchenItems") },
    { key: "specialInstr" as const, label: t("settingsScreen.kitchenSpecial") },
    { key: "estTime" as const, label: t("settingsScreen.kitchenEstTime") },
  ]), [t]);

  const receiptContentRows = useMemo(() => ([
    { key: "storeAddr" as const, label: t("settingsScreen.receiptStoreAddr") },
    { key: "orderDate" as const, label: t("settingsScreen.receiptOrderDate") },
    { key: "itemsPrices" as const, label: t("settingsScreen.receiptItemsPrices") },
    { key: "subtotalEtc" as const, label: t("settingsScreen.receiptSubtotal") },
    { key: "totalAmount" as const, label: t("settingsScreen.receiptTotal") },
    { key: "payMethod" as const, label: t("settingsScreen.receiptPayMethod") },
  ]), [t]);

  // Real stalls and the devices their staff actually signed in from — never sample data.
  const { data: stallsRes } = useAdminStalls();
  const { data: staffRes } = useAdminStaff();
  const stallCards = useMemo(() => (stallsRes?.data ?? []).filter(st => st.is_open !== false).slice(0, 12).map(st => {
    const people = (staffRes?.data ?? []).filter(p => p.stall_id === st.id && p.is_active);
    const latest = people.filter(p => p.last_login_at).sort((a, b) => String(b.last_login_at).localeCompare(String(a.last_login_at)))[0];
    return latest
      ? { name: st.name, linked: true as const, device: latest.tablet_label || latest.name, info: dayjs(latest.last_login_at).format("YYYY-MM-DD HH:mm") }
      : { name: st.name, linked: false as const, device: null, info: null };
  }), [stallsRes, staffRes]);

  /* API */
  const { data, isLoading } = useAdminSettings();
  const update = useUpdateSettings();
  const updateLogo = useUpdateLogo();
  const { data: hoursData } = useKopitiamHours();
  const updateHours = useUpdateHours();
  const settings = data?.data as Record<string, unknown> | undefined;
  // memoised: a NEW object on every render made the form fall back to these values on any re-render (typed text and a looked-up
  // latitude/longitude were wiped as soon as a loading spinner or a hint appeared)
  const initialValues = useMemo(() => settings
    ? { name:settings.name, phone:settings.phone, email:settings.email, address:settings.address, lat:settings.lat, lng:settings.lng, app_version_label:settings.app_version_label }
    : undefined, [settings]);
  // The form is filled ONCE when the settings arrive. (It used to be re-keyed on every settings object change, which re-mounted it and
  // wiped whatever the admin had typed or looked up — e.g. the coordinates found from the address — the moment a refetch landed.)
  const formHydrated = useRef(false);
  useEffect(() => {
    if (!initialValues || formHydrated.current) return;
    form.setFieldsValue(initialValues);
    formHydrated.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);
  const [locating, setLocating] = useState(false);
  // Look the shop's coordinates up from its ADDRESS (Nominatim) — the device-location button only knows where this browser is, not the shop.
  const [finding, setFinding] = useState(false);
  const [geoNote, setGeoNote] = useState<{ kind: "ok" | "approx" | "none"; name?: string; lat?: number; lng?: number } | null>(null);
  const findByAddress = async () => {
    const address = String(form.getFieldValue("address") ?? "").trim();
    if (!address) { void message.error(t("settingsScreen.geoNeedAddress")); return; }
    setFinding(true);
    try {
      let hit = await geocodeWithFallback(address, "my");
      // OSM missing or only area-level → ask Google (server-side, only when a key is configured); keep OSM's answer if Google is no better.
      if (!hit || hit.approximate) {
        const g = await geocodeViaGoogle(address).then((r) => r.data).catch(() => null);
        if (g?.result && (!hit || !g.result.approximate)) hit = { lat: g.result.lat, lng: g.result.lng, displayName: g.result.displayName, approximate: g.result.approximate };
      }
      if (!hit) { setGeoNote({ kind: "none" }); return; }
      form.setFieldsValue({ lat: Number(hit.lat.toFixed(6)), lng: Number(hit.lng.toFixed(6)) });
      setGeoNote({ kind: hit.approximate ? "approx" : "ok", name: hit.displayName, lat: hit.lat, lng: hit.lng });

    } finally { setFinding(false); }
  };
  // Paste "3.0123, 101.6123" (what Google Maps copies on right-click) or a maps URL containing it — far more exact than the OSM address lookup.
  const gKey = useGoogleMapsKey();
  const setGKey = useSetGoogleMapsKey();
  const [gKeyInput, setGKeyInput] = useState("");
  const saveGKey = (key: string | null) => setGKey.mutate(key, {
    onSuccess: () => { setGKeyInput(""); void message.success(t("settingsScreen.gKeySaved")); },
    onError: () => { void message.error(t("settingsScreen.gKeyFailed")); },
  });
  const [pasteBad, setPasteBad] = useState(false);
  const pasteCoords = (raw: string) => {
    const m = raw.match(/(-?\d{1,3}\.\d+)\s*[,\s]\s*(-?\d{1,3}\.\d+)/);
    if (!raw.trim()) { setPasteBad(false); return; }
    const lat = m ? Number(m[1]) : NaN, lng = m ? Number(m[2]) : NaN;
    if (!m || Math.abs(lat) > 90 || Math.abs(lng) > 180) { setPasteBad(true); return; }
    setPasteBad(false);
    form.setFieldsValue({ lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) });
    setGeoNote({ kind: "ok", lat, lng, name: "" });
  };
  const useCurrentLocation = () => {
    if (!navigator.geolocation) { void message.error(t("settingsScreen.geolocationUnsupported", "Location not supported by this browser")); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        form.setFieldsValue({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      () => { void message.error(t("settingsScreen.geolocationFailed", "Couldn't get your location")); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const sstHydrated = useRef(false);
  useEffect(() => {
    if (sstHydrated.current || !settings?.sst_rate) return;
    setSstRate((Number(settings.sst_rate) * 100).toString());
    sstHydrated.current = true;
  }, [settings]);

  const chargeSstHydrated = useRef(false);
  useEffect(() => {
    if (chargeSstHydrated.current || settings?.charge_sst == null) return;
    setChargeSst(Boolean(settings.charge_sst));
    chargeSstHydrated.current = true;
  }, [settings]);

  const deliveryHydrated = useRef(false);
  useEffect(() => {
    if (deliveryHydrated.current || !settings) return;
    if (settings.delivery_fee_default != null) setBaseFee(Number(settings.delivery_fee_default).toFixed(2));
    if (settings.delivery_radius_km != null) setRadius(Number(settings.delivery_radius_km).toString());
    if (settings.delivery_control_enabled != null) setAllowDelivery(Boolean(settings.delivery_control_enabled));
    deliveryHydrated.current = true;
  }, [settings]);

  const hoursHydrated = useRef(false);
  useEffect(() => {
    if (hoursHydrated.current || !hoursData?.data?.length) return;
    setHours(prev => {
      const next = { ...prev };
      for (const row of hoursData.data) {
        const day = DAYS.find(d => DAY_TO_INDEX[d] === row.dayOfWeek);
        if (!day) continue;
        next[day] = {
          enabled: !row.isClosed,
          from: dayjs(row.openTime, "HH:mm"),
          to: dayjs(row.closeTime, "HH:mm"),
        };
      }
      return next;
    });
    hoursHydrated.current = true;
  }, [hoursData]);

  const paymentHydrated = useRef(false);
  useEffect(() => {
    if (paymentHydrated.current || !settings) return;
    if (settings.cash_refund_enabled != null) setCashRefund(Boolean(settings.cash_refund_enabled));
    if (settings.bank_refund_enabled != null) setBankRefund(Boolean(settings.bank_refund_enabled));
    paymentHydrated.current = true;
  }, [settings]);

  // Printer tab config genuinely persists now — hydrate from the saved record,
  // falling back to the hardcoded defaults above when never saved before.
  const printerHydrated = useRef(false);
  useEffect(() => {
    if (printerHydrated.current || !settings) return;
    const p = settings.printer_settings as Record<string, unknown> | null | undefined;
    if (p) {
      if (p.printerName != null) setPrinterName(String(p.printerName));
      if (p.connType != null) setConnType(String(p.connType));
      if (p.printerIp != null) setPrinterIp(String(p.printerIp));
      if (p.paperWidth != null) setPaperWidth(String(p.paperWidth));
      if (p.autoPrintKitchen != null) setAutoPrintKitchen(Boolean(p.autoPrintKitchen));
      if (p.autoPrintReceipt != null) setAutoPrintReceipt(Boolean(p.autoPrintReceipt));
      if (p.manualTrigger != null) setManualTrigger(Boolean(p.manualTrigger));
      if (p.kitchenSlip) setKitchenSlip(prev => ({ ...prev, ...(p.kitchenSlip as Record<KitchenKey, boolean>) }));
      if (p.receiptContent) setReceiptContent(prev => ({ ...prev, ...(p.receiptContent as Record<ReceiptKey, boolean>) }));
    }
    printerHydrated.current = true;
  }, [settings]);

  // Notifications tab config genuinely persists now — but only as admin
  // PREFERENCE data. It does not yet drive real pos-app/stall-app alert
  // behavior, which remains deliberately per-device/local (see
  // useAlertPrefsStore in those apps).
  const notifHydrated = useRef(false);
  useEffect(() => {
    if (notifHydrated.current || !settings) return;
    const n = settings.notification_settings as Record<string, unknown> | null | undefined;
    if (n) {
      if (n.alerts) setAlerts(prev => ({ ...prev, ...(n.alerts as Record<AlertKey, boolean>) }));
      if (n.alertSoundOn != null) setAlertSoundOn(Boolean(n.alertSoundOn));
      if (n.volume != null) setVolume(Number(n.volume));
      if (n.soundTheme != null) setSoundTheme(String(n.soundTheme));
      if (n.tabletPushOn != null) setTabletPushOn(Boolean(n.tabletPushOn));
      if (n.soundAlertOn != null) setSoundAlertOn(Boolean(n.soundAlertOn));
      if (n.allStallNotifs != null) setAllStallNotifs(Boolean(n.allStallNotifs));
      if (n.notifLanguage != null) setNotifLanguage(String(n.notifLanguage));
    }
    notifHydrated.current = true;
  }, [settings]);

  const smsHydrated = useRef(false);
  useEffect(() => {
    if (smsHydrated.current || !settings) return;
    const s = settings.sms_template_settings as { defaultLang?: string; templates?: Record<string, string> } | null | undefined;
    if (s) {
      if (s.defaultLang === "zh" || s.defaultLang === "en" || s.defaultLang === "ms") setSmsDefaultLang(s.defaultLang);
      if (s.templates?.zh != null) setSmsTemplateZh(s.templates.zh);
      if (s.templates?.en != null) setSmsTemplateEn(s.templates.en);
      if (s.templates?.ms != null) setSmsTemplateMs(s.templates.ms);
    }
    smsHydrated.current = true;
  }, [settings]);

  const otpSettingsHydrated = useRef(false);
  useEffect(() => {
    if (otpSettingsHydrated.current || !settings) return;
    const o = settings.otp_settings as { dailyCap?: number; cooldownSeconds?: number; expirySeconds?: number; codeLength?: number } | null | undefined;
    if (o) {
      if (o.dailyCap != null) setOtpDailyCap(String(o.dailyCap));
      if (o.cooldownSeconds != null) setOtpCooldownSeconds(String(o.cooldownSeconds));
      if (o.expirySeconds != null) setOtpExpirySeconds(String(o.expirySeconds));
      if (o.codeLength != null) setOtpCodeLength(String(o.codeLength));
    }
    otpSettingsHydrated.current = true;
  }, [settings]);

  const registrationHydrated = useRef(false);
  useEffect(() => {
    if (registrationHydrated.current || !settings) return;
    const r = settings.registration_settings as { allowEmailRegistration?: boolean; allowPhoneRegistration?: boolean } | null | undefined;
    if (r) {
      if (r.allowEmailRegistration != null) setAllowEmailRegistration(Boolean(r.allowEmailRegistration));
      if (r.allowPhoneRegistration != null) setAllowPhoneRegistration(Boolean(r.allowPhoneRegistration));
    }
    registrationHydrated.current = true;
  }, [settings]);

  const emailHydrated = useRef(false);
  useEffect(() => {
    if (emailHydrated.current || !settings) return;
    const e = settings.email_settings as {
      enabled?: boolean; provider?: "resend" | "smtp"; resendApiKey?: string; fromEmail?: string;
      smtpHost?: string; smtpPort?: number; smtpSecure?: boolean; smtpUser?: string; smtpPass?: string;
    } | null | undefined;
    if (e) {
      if (e.enabled != null) setEmailEnabled(Boolean(e.enabled));
      if (e.provider === "resend" || e.provider === "smtp") setEmailProvider(e.provider);
      if (e.resendApiKey != null) setResendApiKey(e.resendApiKey);
      if (e.fromEmail != null) setEmailFromAddress(e.fromEmail);
      if (e.smtpHost != null) setSmtpHost(e.smtpHost);
      if (e.smtpPort != null) setSmtpPort(String(e.smtpPort));
      if (e.smtpSecure != null) setSmtpSecure(Boolean(e.smtpSecure));
      if (e.smtpUser != null) setSmtpUser(e.smtpUser);
      if (e.smtpPass != null) setSmtpPass(e.smtpPass);
    }
    emailHydrated.current = true;
  }, [settings]);

  const emailTemplateHydrated = useRef(false);
  useEffect(() => {
    if (emailTemplateHydrated.current || !settings) return;
    const et = settings.email_template_settings as { defaultLang?: string; templates?: Record<string, EmailLangTemplate> } | null | undefined;
    if (et) {
      if (et.defaultLang === "zh" || et.defaultLang === "en" || et.defaultLang === "ms") setEmailTemplateDefaultLang(et.defaultLang);
      if (et.templates?.zh) setEmailTemplateZh({ ...EMPTY_EMAIL_TEMPLATE, ...et.templates.zh });
      if (et.templates?.en) setEmailTemplateEn({ ...EMPTY_EMAIL_TEMPLATE, ...et.templates.en });
      if (et.templates?.ms) setEmailTemplateMs({ ...EMPTY_EMAIL_TEMPLATE, ...et.templates.ms });
    }
    emailTemplateHydrated.current = true;
  }, [settings]);

  const updateDay = (day: Day, patch: Partial<DaySchedule>) =>
    setHours(prev => ({ ...prev, [day]:{ ...prev[day], ...patch } }));
  const applyToAllDays = () => {
    const first = DAYS.find(d => hours[d].enabled);
    if (!first) return;
    const { from, to } = hours[first];
    setHours(prev => Object.fromEntries(DAYS.map(d => [d, { ...prev[d], from, to }])) as Record<Day, DaySchedule>);
  };

  // Mirrors PricingService.computeDeliveryFee on the backend exactly — this is
  // the real fallback formula used when no live Lalamove quote is available,
  // not a separate configurable per-km/min/max model (there isn't one).
  const calcFee = (km: number) => {
    const base = Number(baseFee);
    if (!(base >= 0) || base > 200) return "—"; // invalid input is rejected on save — never preview a negative fee
    if (km <= 3) return base.toFixed(2);
    if (km <= 7) return (base + 2).toFixed(2);
    if (km <= 12) return (base + 5).toFixed(2);
    return (base + 8).toFixed(2);
  };

  // Turns a rejected save into a specific, actionable toast instead of the
  // generic "save failed" message — e.g. surfaces the backend's actual
  // validation issue (delivery_radius_km exceeding the 10km cap) so the admin
  // knows exactly what to fix, since a failed save otherwise silently
  // discards every field edited in the same batch (see delivery-zone bug).
  const describeSaveError = useCallback(async (err: unknown): Promise<string> => {
    if (err instanceof HTTPError && err.response.status === 400) {
      try {
        const body = await err.response.json() as {
          data?: { issues?: Array<{ code?: string; path?: Array<string | number>; message?: string; maximum?: number; minimum?: number }> };
        };
        const issues = body?.data?.issues ?? [];
        if (issues.length) {
          return issues.map((issue) => {
            const field = issue.path?.[issue.path.length - 1];
            if (field === "delivery_radius_km" && issue.code === "too_big" && issue.maximum != null) {
              return t("settingsScreen.saveFailedRadiusExceeded", { max: issue.maximum, defaultValue: `最大外送半径不能超过 ${issue.maximum} 公里` });
            }
            return issue.message ? `${String(field ?? "")}: ${issue.message}` : t("settingsScreen.saveFailed");
          }).join("；");
        }
      } catch {
        // Response body already consumed or not JSON — fall through to the generic message below.
      }
    }
    return t("settingsScreen.saveFailed");
  }, [t]);

  const sstErr = sstRate.trim() === "" ? t("settingsScreen.sstRateRequired") : !(Number(sstRate) >= 0 && Number(sstRate) <= 100) ? t("settingsScreen.sstRateRange") : "";
  const baseFeeErr = baseFee.trim() === "" ? t("settingsScreen.baseFeeRequired") : !(Number(baseFee) >= 0) ? t("settingsScreen.baseFeeNotNumber") : Number(baseFee) > 200 ? t("settingsScreen.baseFeeTooHigh") : "";
  const handleSave = useCallback(async () => {
    try {
      const values = await form.validateFields();
      // SST is part of every price: a blank or nonsense rate would silently save 0% (or a huge tax).
      const sstNum = Number(sstRate);
      if (sstRate.trim() === "" || !Number.isFinite(sstNum) || sstNum < 0 || sstNum > 100) {
        void message.error(t("settingsScreen.sstRateInvalid", "SST 税率必填，且必须在 0% 到 100% 之间（不收 SST 请填 0）"));
        return;
      }
      if (baseFeeErr) { void message.error(t("settingsScreen.baseFeeInvalid", "默认外送费必填，且不能是负数")); return; }
      setSaving(true);
      await Promise.all([
        update.mutateAsync({
          name: values.name as string,
          phone: values.phone as string,
          email: values.email as string,
          address: values.address as string,
          lat: values.lat != null ? Number(values.lat) : null,
          lng: values.lng != null ? Number(values.lng) : null,
          app_version_label: values.app_version_label as string,
          delivery_control_enabled: allowDelivery,
          sst_rate: (parseFloat(sstRate) || 0) / 100,
          charge_sst: chargeSst,
          delivery_fee_default: parseFloat(baseFee) || 0,
          delivery_radius_km: parseFloat(radius) || 0,
          cash_refund_enabled: cashRefund,
          bank_refund_enabled: bankRefund,
          printer_settings: {
            printerName, connType, printerIp, paperWidth,
            autoPrintKitchen, autoPrintReceipt, manualTrigger,
            kitchenSlip, receiptContent,
          },
          notification_settings: {
            alerts, alertSoundOn, volume, soundTheme,
            tabletPushOn, soundAlertOn, allStallNotifs, notifLanguage,
          },
          sms_template_settings: {
            defaultLang: smsDefaultLang,
            templates: { zh: smsTemplateZh, en: smsTemplateEn, ms: smsTemplateMs },
          },
          otp_settings: {
            dailyCap: parseInt(otpDailyCap, 10) || 5,
            cooldownSeconds: parseInt(otpCooldownSeconds, 10) || 60,
            expirySeconds: parseInt(otpExpirySeconds, 10) || 300,
            codeLength: parseInt(otpCodeLength, 10) || 6,
          },
          registration_settings: {
            allowEmailRegistration,
            allowPhoneRegistration,
          },
          email_settings: {
            enabled: emailEnabled,
            provider: emailProvider,
            resendApiKey,
            fromEmail: emailFromAddress,
            smtpHost,
            smtpPort: parseInt(smtpPort, 10) || 587,
            smtpSecure,
            smtpUser,
            smtpPass,
          },
          email_template_settings: {
            defaultLang: emailTemplateDefaultLang,
            templates: { zh: emailTemplateZh, en: emailTemplateEn, ms: emailTemplateMs },
          },
        }),
        // Business Hours has its own dedicated, correctly-modeled backend
        // (KopitiamHours / PATCH admin/hours) — PATCH admin/settings has no
        // operating_hours field and would silently drop it (see #240).
        updateHours.mutateAsync(
          DAYS.map(d => ({
            dayOfWeek: DAY_TO_INDEX[d],
            openTime: hours[d].from.format("HH:mm"),
            closeTime: hours[d].to.format("HH:mm"),
            isClosed: !hours[d].enabled,
          })),
        ),
      ]);
      void message.success(t("settingsScreen.saveSuccess"));
    } catch (err) {
      void message.error(await describeSaveError(err));
    } finally {
      setSaving(false);
    }
  }, [
    allowDelivery, baseFee, baseFeeErr, describeSaveError, form, hours, radius, sstRate, chargeSst, t, update, updateHours,
    cashRefund, bankRefund,
    printerName, connType, printerIp, paperWidth, autoPrintKitchen, autoPrintReceipt, manualTrigger, kitchenSlip, receiptContent,
    alerts, alertSoundOn, volume, soundTheme, tabletPushOn, soundAlertOn, allStallNotifs, notifLanguage,
    smsDefaultLang, smsTemplateZh, smsTemplateEn, smsTemplateMs,
    otpDailyCap, otpCooldownSeconds, otpExpirySeconds, otpCodeLength,
    allowEmailRegistration, allowPhoneRegistration,
    emailEnabled, emailProvider, resendApiKey, emailFromAddress,
    smtpHost, smtpPort, smtpSecure, smtpUser, smtpPass,
    emailTemplateDefaultLang, emailTemplateZh, emailTemplateEn, emailTemplateMs,
  ]);

  /* ── Shared input style ──────────────────────────── */
  const inp: React.CSSProperties = { height:44, borderRadius:10, fontSize:14 };

  return (
    <AdminShell>
      <div className="admin-settings-page admin-list-page" style={{ padding:"28px 32px 40px", background:T.bg, minHeight:"100vh" }}>
        <div className="admin-settings-inner" style={{ maxWidth:1480, margin:"0 auto" }}>
      {/* Header */}
      <div className="admin-settings-toolbar" style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", gap:18, flexWrap:"wrap", marginBottom:22 }}>
        <div>
          <h1 className="admin-page-title" style={{ margin:0, fontSize:32, lineHeight:1.1, fontWeight:900, color:T.textDark }}>{t("settingsScreen.title")}</h1>
          <p className="admin-settings-subtitle">{t("settingsScreen.subtitle")}</p>
        </div>
        <Button type="primary" className="admin-settings-save" onClick={() => void handleSave()} loading={saving} disabled={!!(sstErr || baseFeeErr)}
          style={{ height:44, borderRadius:12, fontWeight:800, fontSize:15, background:T.brand, borderColor:T.brand, color:T.surface, paddingInline:24, boxShadow:"0 12px 24px rgba(249,115,22,0.18)" }}>
          {t("settingsScreen.saveChanges")}
        </Button>
      </div>

      {/* Tab bar */}
      <nav className="admin-tabs admin-settings-tabs" aria-label={t("settingsScreen.title")} style={{ display:"flex", borderBottom:"1px solid #E5E7EB", marginBottom:28, overflowX:"auto", background:T.surface, border:"1px solid #E5E7EB", borderRadius:16, padding:"4px 8px 0", boxShadow:"0 12px 28px rgba(15,23,42,0.04)" }}>
        {tabs.map(tab => (
          <button className="admin-settings-tab" key={tab.key} type="button" aria-pressed={activeTab === tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding:"10px 18px", border:"none", background:"transparent", cursor:"pointer",
            fontSize:14, fontWeight:activeTab===tab.key ? 700 : 500,
            color:activeTab===tab.key ? T.brand : T.textBody,
            borderBottom:activeTab===tab.key ? "2px solid #F97316" : "2px solid transparent",
            marginBottom:-1, transition:"all .15s", flexShrink:0, whiteSpace:"nowrap",
          }}>
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="admin-settings-content">

      {/* ════════════════════════════════════════════════ */}
      {/* STORE INFO TAB                                  */}
      {/* ════════════════════════════════════════════════ */}
      {activeTab === "store" && (
        <div className="admin-settings-columns" style={{ display:"flex", gap:20, alignItems:"flex-start", flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <SectionCard icon={<InfoCircleOutlined />} title={t("settingsScreen.sectionBasicInfo")}>
              <Form form={form} layout="vertical" initialValues={initialValues} disabled={isLoading}>
                <Form.Item name="name" label={fieldLabel("lblStoreName")}
                  rules={[{ required:true, message:t("settingsScreen.storeNameRequired") }]} style={{ marginBottom:14 }}>
                  <Input style={inp} />
                </Form.Item>
                <div className="admin-settings-field-grid" style={{ display:"flex", gap:12, marginBottom:14, flexWrap:"wrap" }}>
                  <Form.Item name="phone" label={fieldLabel("lblPhone")} style={{ flex:"1 1 160px", minWidth:160, marginBottom:0 }}>
                    <Input placeholder={t("settingsScreen.phPhone")} style={inp} />
                  </Form.Item>
                  <Form.Item name="email" label={fieldLabel("lblEmail")} style={{ flex:"1 1 160px", minWidth:160, marginBottom:0 }}>
                    <Input placeholder={t("settingsScreen.phEmail")} style={inp} />
                  </Form.Item>
                </div>
                <Form.Item name="address" label={fieldLabel("lblAddress")} style={{ marginBottom:14 }}>
                  <Input.TextArea rows={3} style={{ borderRadius:10, fontSize:14, resize:"none" }} />
                </Form.Item>
                <div className="admin-settings-location-grid" style={{ display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap" }}>
                  <Form.Item name="lat" label={fieldLabel("lblLatitude")} style={{ flex:"1 1 140px", minWidth:140, marginBottom:0 }}>
                    <InputNumber style={{ ...inp, width:"100%" }} placeholder="3.1390" step="0.000001" />
                  </Form.Item>
                  <Form.Item name="lng" label={fieldLabel("lblLongitude")} style={{ flex:"1 1 140px", minWidth:140, marginBottom:0 }}>
                    <InputNumber style={{ ...inp, width:"100%" }} placeholder="101.6869" step="0.000001" />
                  </Form.Item>
                  <Button type="primary" onClick={() => void findByAddress()} loading={finding} style={{ height:44, borderRadius:10, whiteSpace:"nowrap", flex:"1 1 auto" }}>
                    {t("settingsScreen.findByAddress")}
                  </Button>
                  <Button onClick={useCurrentLocation} loading={locating} style={{ height:44, borderRadius:10, whiteSpace:"nowrap", flex:"1 1 auto" }}>
                    {t("settingsScreen.useCurrentLocation", "Use current location")}
                  </Button>
                </div>
                <Input allowClear placeholder={t("settingsScreen.pasteCoordsPh")} onChange={(e) => pasteCoords(e.target.value)} status={pasteBad ? "error" : undefined} style={{ ...inp, marginTop:10 }} />
                <div style={{ fontSize:12, marginTop:4, color: pasteBad ? "#991B1B" : T.textMuted }}>{pasteBad ? t("settingsScreen.pasteCoordsBad") : t("settingsScreen.pasteCoordsHint")}</div>
                {geoNote && (
                  <div data-testid="geo-note" style={{ marginTop:10, padding:"10px 12px", borderRadius:10, fontSize:13, lineHeight:1.5, fontWeight: geoNote.kind === "approx" ? 700 : undefined, border: geoNote.kind === "approx" ? "2px solid #F59E0B" : undefined,
                    background: geoNote.kind === "ok" ? "#ECFDF5" : geoNote.kind === "approx" ? "#FFFBEB" : "#FEF2F2",
                    color: geoNote.kind === "ok" ? "#065F46" : geoNote.kind === "approx" ? "#92400E" : "#991B1B" }}>
                    {geoNote.kind === "none" ? t("settingsScreen.geoNone") : (<>
                      {geoNote.kind === "approx" ? t("settingsScreen.geoApprox") : t("settingsScreen.geoFound")}
                      <div style={{ opacity:0.85, marginTop:2 }}>{geoNote.name}</div>
                      <a href={`https://www.google.com/maps?q=${geoNote.lat},${geoNote.lng}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight:700 }}>{t("settingsScreen.geoOpenMap")}</a>
                      {" · "}{t("settingsScreen.geoSaveHint")}
                    </>)}
                  </div>
                )}
                <div data-testid="google-key" style={{ marginTop:12, padding:"10px 12px", borderRadius:10, border:"1px solid #e5e7eb", background:"#fafafa" }}>
                  <div style={{ fontSize:13, fontWeight:700 }}>{t("settingsScreen.gKeyTitle")}</div>
                  <div style={{ fontSize:12, color:T.textMuted, margin:"2px 0 8px" }}>
                    {gKey.data?.data.set ? t("settingsScreen.gKeyOn", { last4: gKey.data.data.last4 }) : t("settingsScreen.gKeyOff")}
                  </div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    <Input.Password autoComplete="off" value={gKeyInput} onChange={(e) => setGKeyInput(e.target.value)} placeholder={t("settingsScreen.gKeyPh")} style={{ ...inp, flex:"1 1 240px" }} />
                    <Button type="primary" disabled={!gKeyInput.trim()} loading={setGKey.isPending} onClick={() => saveGKey(gKeyInput.trim())} style={{ height:44, borderRadius:10 }}>{gKey.data?.data.set ? t("settingsScreen.gKeyReplace") : t("settingsScreen.gKeySave")}</Button>
                    {gKey.data?.data.set && <Button danger loading={setGKey.isPending} onClick={() => saveGKey(null)} style={{ height:44, borderRadius:10 }}>{t("settingsScreen.gKeyRemove")}</Button>}
                  </div>
                </div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:6, marginBottom:14 }}>
                  {t("settingsScreen.locationHint", "Real pickup coordinates for this food court — used as the delivery-quote origin so Lalamove fees and dispatch reflect the real distance instead of a placeholder.")}
                </div>
                <Form.Item name="app_version_label" label={t("settingsScreen.lblAppVersion", "App Version Label")} style={{ marginBottom:0 }}>
                  <Input placeholder="2.4.0" style={inp} />
                </Form.Item>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:6 }}>
                  {t("settingsScreen.appVersionHint", "Shown in the customer-app profile screen and pos-app login footer.")}
                </div>
              </Form>
            </SectionCard>

            <SectionCard icon={<ShoppingOutlined />} title={t("settingsScreen.sectionOrderTypes")}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontWeight:700, color:T.textDark, fontSize:14, marginBottom:4 }}>{t("settingsScreen.allowDeliveryTitle")}</div>
                  <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.allowDeliveryDesc")}</div>
                </div>
                <Switch checked={allowDelivery} onChange={setAllowDelivery} />
              </div>
            </SectionCard>

            <SectionCard icon={<PictureOutlined />} title={t("settingsScreen.sectionBranding")}>
              <div className="admin-settings-brand-row" style={{ display:"flex", alignItems:"center", gap:18 }}>
                <div style={{ width:80, height:80, borderRadius:10, overflow:"hidden", flexShrink:0 }}>
                  <ImageUpload
                    folder="branding"
                    value={settings?.logo_url as string | undefined}
                    onChange={(url) => {
                      updateLogo.mutateAsync(url)
                        .then(() => void message.success(t("settingsScreen.logoUpdated", "Logo updated")))
                        .catch(() => void message.error(t("settingsScreen.logoUpdateFailed", "Failed to save logo")));
                    }}
                  />
                </div>
                <div>
                  <div style={{ fontWeight:700, color:T.textDark, fontSize:14, marginBottom:4 }}>{t("settingsScreen.brandLogoTitle")}</div>
                  <div style={{ fontSize:12, color:T.textMuted, marginBottom:12 }}>
                    {t("settingsScreen.brandLogoHint")}
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <div style={{ width:390, maxWidth:"100%", flexShrink:0 }}>
            <SectionCard icon={<ClockCircleOutlined />} title={t("settingsScreen.sectionHours")}
              extra={
                <button type="button" onClick={applyToAllDays} style={{
                  border:"1px solid #166534", borderRadius:8, background:"transparent",
                  color:T.successText, fontSize:12, fontWeight:600, padding:"5px 12px", cursor:"pointer",
                }}>
                  {t("settingsScreen.applyAllDays")}
                </button>
              }
            >
              <div className="admin-settings-hours-list" style={{ display:"flex", flexDirection:"column", gap:14 }}>
                {DAYS.map(day => {
                  const d = hours[day];
                  return (
                    <div className="admin-settings-hours-row" key={day} style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                      <Switch size="small" checked={d.enabled} onChange={v => updateDay(day,{ enabled:v })} />
                      <span style={{ width:92, fontSize:13, fontWeight:600, flexShrink:0, color:d.enabled ? T.textDark : T.textMuted }}>
                        {t(`hours.days.${day}`)}
                      </span>
                      <div className="admin-settings-hours-time-range">
                        <TimePicker size="small" value={d.from} format="hh:mm A" disabled={!d.enabled} needConfirm={false}
                          onChange={v => { if (v) updateDay(day,{ from:v }); }} style={{ width:100, borderRadius:8 }} />
                        <span style={{ color:T.textMuted, fontSize:13, flexShrink:0 }}>—</span>
                        <TimePicker size="small" value={d.to} format="hh:mm A" disabled={!d.enabled} needConfirm={false}
                          onChange={v => { if (v) updateDay(day,{ to:v }); }} style={{ width:100, borderRadius:8 }} />
                      </div>
                      <span style={{ padding:"2px 8px", borderRadius:6, fontSize:11, fontWeight:700, minWidth:58, textAlign:"center", flexShrink:0,
                        background:d.enabled ? T.successText : T.surfaceSection, color:d.enabled ? T.surface : T.textMuted }}>
                        {d.enabled ? t("settingsScreen.open") : t("settingsScreen.closed")}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop:16, padding:"10px 14px", background:T.bg, borderRadius:10, fontSize:12, color:T.textMuted }}>
                {t("settingsScreen.holidayHintBefore")}{" "}
                <span style={{ color:T.brand, fontWeight:600, cursor:"pointer" }}>{t("settingsScreen.holidayHintLink")}</span>{" "}
                {t("settingsScreen.holidayHintAfter")}
              </div>
            </SectionCard>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════ */}
      {/* DELIVERY TAB                                    */}
      {/* ════════════════════════════════════════════════ */}
      {activeTab === "delivery" && (
        <>
          <div className="admin-settings-split" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
            {/* Left */}
            <div style={{ flex:1, minWidth:0 }}>
              <SectionCard icon={<CarOutlined />} title={t("settingsScreen.sectionDeliveryFee")}>
                <div style={{ flex:1 }}>
                  {fieldLabel("lblBaseFee")}
                  <Input prefix="RM" value={baseFee} onChange={e => setBaseFee(e.target.value)}
                    style={{ ...inp, marginTop:6, ...(baseFeeErr ? { borderColor:"#dc2626" } : {}) }} />
                  {baseFeeErr && <div style={{ fontSize:12, color:"#dc2626", marginTop:4, fontWeight:600 }}>{baseFeeErr}</div>}
                  <div style={{ fontSize:11, color:T.textMuted, marginTop:4 }}>{t("settingsScreen.hintBaseFee")}</div>
                </div>

                {/* Fee preview */}
                <div style={{ marginTop:18, background:T.bg, borderRadius:10, padding:"14px 16px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                    <span style={{ fontWeight:700, fontSize:14, color:T.textDark }}>{t("settingsScreen.feePreview")}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:T.textMuted, letterSpacing:0.5 }}>{t("settingsScreen.dynamicEstimate")}</span>
                  </div>
                  {[2, 5, 10].map(km => {
                    const maxRadius = parseFloat(radius);
                    const outOfRange = !Number.isNaN(maxRadius) && maxRadius > 0 && km > maxRadius;
                    return (
                      <div key={km} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderTop:"1px solid #E5E7EB", opacity: outOfRange ? 0.55 : 1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                          <EnvironmentOutlined style={{ color:T.textMuted, fontSize:12 }} />
                          <span style={{ fontSize:14, color:T.textBody }}>{t("settingsScreen.kmUnit", { n: km })}</span>
                          {outOfRange && (
                            <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:4, background:T.dangerBg, color:T.dangerText, whiteSpace:"nowrap" }}>
                              {t("settingsScreen.outOfRange")}
                            </span>
                          )}
                        </div>
                        <span style={{ fontWeight:700, color: outOfRange ? T.textMuted : T.brand }}>RM {calcFee(km)}</span>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            </div>

            {/* Right */}
            <div style={{ width:290, maxWidth:"100%", flexShrink:0 }}>
              <SectionCard icon={<EnvironmentOutlined />} title={t("settingsScreen.sectionDeliveryRadius")}>
                {fieldLabel("lblMaxRadius")}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6 }}>
                  <Input value={radius} onChange={e => setRadius(e.target.value)}
                    style={{ ...inp, flex:1 }} />
                  <span style={{ fontWeight:600, color:T.textBody, fontSize:14 }}>{t("settingsScreen.kmSuffix")}</span>
                </div>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:8 }}>
                  {t("settingsScreen.hintRadius")}
                </div>
              </SectionCard>

              <SectionCard icon={<UserOutlined />} title={t("settingsScreen.sectionRiderAssignment")}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  {fieldLabel("lblMethod")}
                  <span style={{ marginLeft:"auto", fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, background:T.surfaceSection, color:T.textMuted }}>
                    {t("settingsScreen.readOnly")}
                  </span>
                </div>
                <div style={{ fontSize:14, fontWeight:600, color:T.textDark, marginBottom:12 }}>
                  {t("settingsScreen.riderMethodManual")}
                </div>
                <div style={{ background:T.surfaceBlue, borderRadius:10, padding:"10px 14px", fontSize:12, color:T.infoText, display:"flex", gap:8 }}>
                  <InfoCircleOutlined style={{ flexShrink:0, marginTop:1 }} />
                  <span>{t("settingsScreen.riderGpsNote")}</span>
                </div>
              </SectionCard>
            </div>
          </div>

          {/* Third-party delivery integration — configure from the real Delivery Providers screen,
              not a second, un-persisted copy of the same form here (all providers, not just GrabExpress). */}
          <div style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px" }}>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:16, fontWeight:800, color:T.textDark, marginBottom:4 }}>{t("settingsScreen.thirdPartyTitle")}</div>
              <div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.thirdPartySubtitle")}</div>
            </div>
            <div style={{ border:"1px dashed #E5E7EB", borderRadius:12, padding:"16px 18px", display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", textAlign:"center", gap:10 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:T.brand, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <CarOutlined style={{ color:T.surface }} />
              </div>
              <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5 }}>
                {t("settingsScreen.otherProvidersHint", "Configure Lalamove, GrabExpress, and other delivery providers, including sandbox/production credentials and connection testing, from the Delivery Providers screen.")}
              </div>
              <Button onClick={() => navigate("/delivery-providers")} style={{ borderRadius:8, background:T.brand, borderColor:T.brand, color:T.surface, fontWeight:600 }}>
                {t("settingsScreen.goToDeliveryProviders", "Go to Delivery Providers")}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════ */}
      {/* TAX & CURRENCY TAB                              */}
      {/* ════════════════════════════════════════════════ */}
      {activeTab === "tax" && (
        <>
          {/* SST configuration */}
          <section className="admin-settings-panel" style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px", marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:T.textDark, marginBottom:4 }}>{t("settingsScreen.sstConfigTitle")}</div>
                <div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.sstConfigSubtitle")}</div>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:13, color:T.textBody }}>{t("settingsScreen.chargeSstLabel")}</span>
                <Switch checked={chargeSst} onChange={setChargeSst} />
              </div>
            </div>
            <div className="admin-settings-columns" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
                {fieldLabel("lblSstRate")}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:8 }}>
                  <Input value={sstRate} onChange={e => setSstRate(e.target.value)}
                    style={{ ...inp, maxWidth:200, ...(sstErr ? { borderColor:"#dc2626" } : {}) }} />
                  <span style={{ fontWeight:700, fontSize:16, color:T.textBody }}>%</span>
                </div>
                {sstErr && <div style={{ fontSize:12, color:"#dc2626", marginTop:4, fontWeight:600 }}>{sstErr}</div>}
              </div>
              <div style={{ flex:1, background:T.bg, borderRadius:10, padding:"14px 16px" }}>
                <div style={{ fontSize:11, fontWeight:800, letterSpacing:0.5, color:T.textMuted, marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
                  <InfoCircleOutlined style={{ color:T.brand }} /> {t("settingsScreen.calcFormula")}
                </div>
                <div style={{ fontSize:14, color:T.textDark, fontWeight:600, marginBottom:10 }}>
                  {t("settingsScreen.calcFormulaBody")}
                </div>
                <div style={{ fontSize:11, color:T.textMuted, fontWeight:700, marginBottom:6 }}>{t("settingsScreen.exampleLabel")}</div>
                <div style={{ fontSize:12, color:T.textBody }}>
                  {t("settingsScreen.exampleBody", { rate: sstRate })}{" "}
                  <span style={{ color:T.brand, fontWeight:700 }}>
                    RM {(Math.ceil(Math.round(10 * (1 + (parseFloat(sstRate) || 0) / 100) * 1e6) / 1e5) / 10).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Currency display */}
          <section className="admin-settings-panel" style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px", marginBottom:20 }}>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:16, fontWeight:800, color:T.textDark, marginBottom:4 }}>{t("settingsScreen.currencyTitle")}</div>
              <div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.currencySubtitle")}</div>
            </div>
            <div className="admin-settings-currency-grid" style={{ display:"flex", gap:12, marginBottom:14 }}>
              <div style={{ flex:1 }}>
                {fieldLabel("lblCurrency")}
                <Input value={t("settingsScreen.currencyReadonly")} readOnly style={{ ...inp, marginTop:6, background:T.bg }} />
              </div>
              <div style={{ flex:1 }}>
                {fieldLabel("lblDisplayFormat")}
                <Input value={t("settingsScreen.displayFormatSample")} readOnly style={{ ...inp, marginTop:6, background:T.bg }} />
              </div>
              <div style={{ flex:1 }}>
                {fieldLabel("lblStorageFormat")}
                <Input value={t("settingsScreen.storageFormatSample")} readOnly style={{ ...inp, marginTop:6, background:T.bg }} />
              </div>
            </div>
            <div style={{ fontSize:12, color:T.textMuted, display:"flex", alignItems:"center", gap:6 }}>
              <LockOutlined />
              {t("settingsScreen.currencyLockedNote")}
            </div>
          </section>

          {/* Help banner */}
          <div className="admin-settings-help" style={{ background:T.brandLight, borderRadius:14, border:"1px solid #FED7AA", padding:"16px 22px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:T.brand, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <PercentageOutlined style={{ color:T.surface }} />
              </div>
              <div>
                <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{t("settingsScreen.taxHelpTitle")}</div>
                <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.taxHelpSubtitle")}</div>
              </div>
            </div>
            <Button style={{ borderRadius:8, borderColor:T.brand, color:T.brand, fontWeight:700, height:38 }}>
              {t("settingsScreen.learnMore")}
            </Button>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════ */}
      {/* PAYMENT TAB                                     */}
      {/* ════════════════════════════════════════════════ */}
      {activeTab === "payment" && (
        <>
          {/* Payment methods — configure from the real Payment Methods screen, not a second copy of the same toggles here */}
          <section className="admin-settings-panel admin-settings-empty-panel" style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px", marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:T.textDark, marginBottom:4 }}>{t("settingsScreen.payMethodsTitle")}</div>
                <div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.payMethodsSubtitle")}</div>
              </div>
              <div style={{ width:36, height:36, borderRadius:8, background:T.brandLight, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <WalletOutlined style={{ color:T.brand }} />
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:10, padding:"18px 0" }}>
              <div style={{ fontSize:13, color:T.textMuted, lineHeight:1.5, maxWidth:420 }}>
                {t("settingsScreen.payMethodsMovedHint", "Enable or disable cash, e-wallet, card, and FPX for online checkout — including per-method gateway credentials — from the Payment Methods screen.")}
              </div>
              <Button onClick={() => navigate("/payment-methods")} style={{ borderRadius:8, background:T.brand, borderColor:T.brand, color:T.surface, fontWeight:600 }}>
                {t("settingsScreen.goToPaymentMethods", "Go to Payment Methods")}
              </Button>
            </div>
          </section>

          {/* Transaction settings */}
          <h2 className="admin-settings-standalone-title" style={{ fontSize:18, fontWeight:800, color:T.textDark, marginBottom:16 }}>{t("settingsScreen.transactionSettingsTitle")}</h2>
          <section className="admin-settings-panel" style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px" }}>
            <div className="admin-settings-transaction-notes" style={{ display:"flex", gap:16, marginBottom:20 }}>
              <div style={{ flex:1, background:T.bg, borderRadius:10, padding:"12px 16px", display:"flex", gap:10 }}>
                <div style={{ width:28, height:28, borderRadius:6, background:T.successBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <PercentageOutlined style={{ color:T.successText, fontSize:14 }} />
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:T.textMuted, letterSpacing:0.5, marginBottom:2 }}>{t("settingsScreen.txnRecordedLabel")}</div>
                  <div style={{ fontSize:12, color:T.textBody }}>{t("settingsScreen.txnRecordedDesc")}</div>
                </div>
              </div>
              <div style={{ flex:1, background:T.bg, borderRadius:10, padding:"12px 16px", display:"flex", gap:10 }}>
                <div style={{ width:28, height:28, borderRadius:6, background:T.dangerBg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <CloseCircleOutlined style={{ color:T.dangerText, fontSize:14 }} />
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:800, color:T.textMuted, letterSpacing:0.5, marginBottom:2 }}>{t("settingsScreen.failedPayLabel")}</div>
                  <div style={{ fontSize:12, color:T.textBody }}>{t("settingsScreen.failedPayDesc")}</div>
                </div>
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"14px 0", borderTop:"1px solid #F3F4F6" }}>
              <div>
                <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{t("settingsScreen.cashRefundTitle")}</div>
                <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.cashRefundDesc")}</div>
              </div>
              <Switch checked={cashRefund} onChange={setCashRefund} />
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"14px 0", borderTop:"1px solid #F3F4F6" }}>
              <div>
                <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{t("settingsScreen.bankRefundTitle")}</div>
                <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.bankRefundDesc")}</div>
              </div>
              <Switch checked={bankRefund} onChange={setBankRefund} />
            </div>
            <div style={{ fontSize:12, color:T.textMuted, marginTop:8 }}>{t("settingsScreen.refundsManualNote")}</div>
          </section>
        </>
      )}

      {/* ════════════════════════════════════════════════ */}
      {/* PRINTER TAB                                     */}
      {/* ════════════════════════════════════════════════ */}
      {activeTab === "printer" && (
        <>
          <div className="admin-settings-split" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
            {/* Left */}
            <div style={{ flex:1, minWidth:0 }}>
              <section className="admin-settings-panel" style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px", marginBottom:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <PrinterOutlined style={{ color:T.brand, fontSize:18 }} />
                    <span style={{ fontSize:16, fontWeight:800, color:T.textDark }}>{t("settingsScreen.printerSetup")}</span>
                  </div>
                  <Button style={{ borderRadius:8, borderColor:T.brand, color:T.brand, fontWeight:600 }}
                    onClick={() => void handleTestPrint({ printerName, connType, printerIp, paperWidth })}
                  >
                    {t("settingsScreen.testPrint")}
                  </Button>
                </div>
                <div style={{ display:"flex", gap:12, marginBottom:12, flexWrap:"wrap" }}>
                  <div style={{ flex:"1 1 160px", minWidth:160 }}>
                    {fieldLabel("lblPrinterName")}
                    <Input value={printerName} onChange={e => setPrinterName(e.target.value)} style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ flex:"1 1 160px", minWidth:160 }}>
                    {fieldLabel("lblConnType")}
                    <Select value={connType} onChange={setConnType} style={{ width:"100%", marginTop:6, height:44 }}>
                      <Select.Option value="network">{t("settingsScreen.connNetwork")}</Select.Option>
                      <Select.Option value="usb">{t("settingsScreen.connUsb")}</Select.Option>
                      <Select.Option value="bluetooth">{t("settingsScreen.connBluetooth")}</Select.Option>
                    </Select>
                  </div>
                </div>
                <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
                  <div style={{ flex:"1 1 160px", minWidth:160 }}>
                    {fieldLabel("lblIpAddress")}
                    <Input value={printerIp} onChange={e => setPrinterIp(e.target.value)} style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ flex:"1 1 160px", minWidth:160 }}>
                    {fieldLabel("lblPaperWidth")}
                    <Select value={paperWidth} onChange={setPaperWidth} style={{ width:"100%", marginTop:6, height:44 }}>
                      <Select.Option value="58mm">58mm</Select.Option>
                      <Select.Option value="80mm">80mm</Select.Option>
                    </Select>
                  </div>
                </div>
              </section>

              <SectionCard icon={<PrinterOutlined />} title={t("settingsScreen.autoPrintTitle")}>
                {autoPrintRows.map((item, i) => (
                  <div key={item.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start",
                    paddingTop: i > 0 ? 14 : 0, paddingBottom:14, borderBottom: i < 2 ? "1px solid #F3F4F6" : "none" }}>
                    <div>
                      <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{item.name}</div>
                      <div style={{ fontSize:12, color:T.textMuted }}>{item.desc}</div>
                    </div>
                    <Switch checked={item.state} onChange={item.setState} />
                  </div>
                ))}
              </SectionCard>
            </div>

            {/* Right */}
            <div style={{ width:290, maxWidth:"100%", flexShrink:0 }}>
              <SectionCard icon={<PrinterOutlined />} title={t("settingsScreen.kitchenSlipTitle")}>
                {kitchenSlipRows.map(item => (
                  <div key={item.key} style={{
                    padding:"8px 10px", borderRadius:8, marginBottom:6,
                    border: item.key==="itemsCustom" ? "2px solid #F97316" : "1px solid #F3F4F6",
                    background: item.key==="itemsCustom" ? T.brandLight : T.surface,
                  }}>
                    <Checkbox
                      checked={kitchenSlip[item.key]}
                      onChange={e => setKitchenSlip(prev => ({ ...prev, [item.key]:e.target.checked }))}
                    >
                      <span style={{ fontSize:13, fontWeight:600, color:T.textDark }}>{item.label}</span>
                    </Checkbox>
                  </div>
                ))}
              </SectionCard>

              <SectionCard icon={<PrinterOutlined />} title={t("settingsScreen.receiptContentTitle")}>
                {receiptContentRows.map(item => (
                  <div key={item.key} style={{ padding:"8px 10px", borderRadius:8, marginBottom:6, border:"1px solid #F3F4F6" }}>
                    <Checkbox
                      checked={receiptContent[item.key]}
                      onChange={e => setReceiptContent(prev => ({ ...prev, [item.key]:e.target.checked }))}
                    >
                      <span style={{ fontSize:13, fontWeight:600, color:T.textDark }}>{item.label}</span>
                    </Checkbox>
                  </div>
                ))}
              </SectionCard>
            </div>
          </div>

          {/* Live Receipt Preview */}
          <div className="admin-settings-receipt-preview" style={{ background:"#2D2D2D", borderRadius:14, padding:"32px", textAlign:"center", marginTop:4 }}>
            <PrinterOutlined style={{ fontSize:28, color:T.brand, marginBottom:8 }} />
            <div style={{ fontSize:18, fontWeight:800, color:T.surface, marginBottom:6 }}>{t("settingsScreen.livePreviewTitle")}</div>
            <div style={{ fontSize:13, color:T.textMuted, marginBottom:24 }}>
              {t("settingsScreen.livePreviewSubtitle")}
            </div>
            <div style={{ background:T.surface, borderRadius:8, padding:"20px 24px", maxWidth:280, margin:"0 auto", textAlign:"left" }}>
              <div style={{ textAlign:"center", fontWeight:800, fontSize:14, marginBottom:2 }}>{t("settingsScreen.previewStoreName")}</div>
              <div style={{ textAlign:"center", fontSize:11, color:T.textMuted, marginBottom:14 }}>{t("settingsScreen.previewStoreAddr")}</div>
              <div style={{ borderTop:"1px dashed #E5E7EB", paddingTop:10 }}>
                {[
                  { name: t("settingsScreen.previewItem1"), note: t("settingsScreen.previewItem1Note"), price: t("settingsScreen.previewPrice1") },
                  { name: t("settingsScreen.previewItem2"), note: null as string | null, price: t("settingsScreen.previewPrice2") },
                ].map((item, i) => (
                  <div key={i} style={{ marginBottom:8 }}>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:12 }}>{item.name}</span>
                      <span style={{ fontSize:12 }}>{item.price}</span>
                    </div>
                    {item.note && <div style={{ fontSize:11, color:T.textMuted }}>{item.note}</div>}
                  </div>
                ))}
              </div>
              <div style={{ borderTop:"2px solid #212121", paddingTop:8, display:"flex", justifyContent:"space-between", fontWeight:800, fontSize:13, marginBottom:14 }}>
                <span>{t("settingsScreen.previewTotal")}</span><span>{t("settingsScreen.previewTotalAmount")}</span>
              </div>
              <div style={{ textAlign:"center", fontSize:11, color:T.textMuted }}>{t("settingsScreen.previewThankYou")}</div>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════ */}
      {/* NOTIFICATIONS TAB                               */}
      {/* ════════════════════════════════════════════════ */}
      {activeTab === "notifications" && (
        <>
          <div className="admin-settings-split" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
            {/* Left */}
            <div style={{ flex:1, minWidth:0 }}>
              <SectionCard icon={<BellOutlined />} title={t("settingsScreen.orderAlertsTitle")}
                extra={<div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.orderAlertsExtra")}</div>}
              >
                {alertItems.map((item, i) => (
                  <div key={item.key} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0",
                    borderTop: i > 0 ? "1px solid #F3F4F6" : "none" }}>
                    <div style={{ width:40, height:40, borderRadius:10, background:item.iconBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                      {item.icon}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, color:T.textDark, fontSize:14, marginBottom:4 }}>{item.name}</div>
                      <div style={{ display:"flex", gap:6 }}>
                        {item.tags.map(tag => (
                          <span key={tag.label} style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:20, background:tag.bg, color:tag.color }}>
                            {tag.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Switch checked={alerts[item.key]} onChange={v => setAlerts(prev => ({ ...prev, [item.key]:v }))} />
                  </div>
                ))}
              </SectionCard>

              {/* Stall notifications */}
              <div style={{ background:T.surface, borderRadius:14, border:"1px solid #F3F4F6", padding:"20px 22px" }}>
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:16, fontWeight:800, color:T.textDark, marginBottom:4 }}>{t("settingsScreen.stallNotifTitle")}</div>
                  <div style={{ fontSize:13, color:T.textMuted }}>
                    {t("settingsScreen.stallNotifDesc")}
                  </div>
                </div>
                {/* Flow */}
                <div style={{ background:T.surfaceBlue, borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:8, marginBottom:16, flexWrap:"wrap" }}>
                  <TeamOutlined style={{ color:T.infoText }} />
                  <span style={{ fontSize:12, color:T.textBody }}>{t("settingsScreen.flowAccepted")}</span>
                  <span style={{ color:T.textMuted }}>→</span>
                  <span style={{ fontSize:12, color:T.textBody }}>{t("settingsScreen.flowFilters")}</span>
                  <span style={{ color:T.textMuted }}>→</span>
                  <span style={{ fontSize:12, fontWeight:700, color:T.infoText }}>{t("settingsScreen.flowEachNotified")}</span>
                </div>
                {/* Stall cards */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
                  {stallCards.map(stall => (
                    <div key={stall.name} style={{ border:"1px solid #E5E7EB", borderRadius:10, padding:"12px 14px", borderLeft:"3px solid #166534" }}>
                      <div style={{ fontWeight:700, color:T.textDark, fontSize:13, marginBottom:4 }}>{stall.name}</div>
                      {stall.linked ? (
                        <>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <MobileOutlined style={{ fontSize:11, color:T.textMuted }} />
                            <span style={{ fontSize:11, color:T.textBody }}>{stall.device}</span>
                          </div>
                          <div style={{ fontSize:10, color:T.textMuted, marginTop:2 }}>{stall.info}</div>
                        </>
                      ) : (
                        <div style={{ textAlign:"center", padding:"8px 0" }}>
                          <div style={{ fontSize:11, fontWeight:700, color:T.dangerText, marginBottom:4 }}>{t("settingsScreen.notLinked")}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderTop:"1px solid #F3F4F6" }}>
                  <div>
                    <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{t("settingsScreen.enableAllStallsTitle")}</div>
                    <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.enableAllStallsDesc")}</div>
                  </div>
                  <Switch checked={allStallNotifs} onChange={setAllStallNotifs} />
                </div>
                <div style={{ marginTop:12 }}>
                  {fieldLabel("lblNotifLang")}
                  <Select value={notifLanguage} onChange={setNotifLanguage} style={{ width:"100%", marginTop:6, height:44 }}>
                    <Select.Option value="english">{t("settingsScreen.langEnglish")}</Select.Option>
                    <Select.Option value="malay">{t("settingsScreen.langMalay")}</Select.Option>
                  </Select>
                </div>
              </div>
            </div>

            {/* Right */}
            <div style={{ width:290, maxWidth:"100%", flexShrink:0 }}>
              <SectionCard icon={<SoundOutlined />} title={t("settingsScreen.alertSoundTitle")}
                extra={<Switch checked={alertSoundOn} onChange={setAlertSoundOn} />}
              >
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                  {fieldLabel("lblVolume")}
                  <span style={{ fontSize:13, fontWeight:700, color:T.brand }}>{volume}%</span>
                </div>
                <Slider min={0} max={100} value={volume} onChange={setVolume}
                  style={{ marginBottom:16 }}
                  styles={{ track:{ background:T.brand }, handle:{ borderColor:T.brand } }}
                />
                <div style={{ marginBottom:16 }}>
                  {fieldLabel("lblSoundTheme")}
                  <Select value={soundTheme} onChange={setSoundTheme} style={{ width:"100%", marginTop:6, height:44 }}>
                    <Select.Option value="chime">{t("settingsScreen.soundChime")}</Select.Option>
                    <Select.Option value="bell">{t("settingsScreen.soundBell")}</Select.Option>
                    <Select.Option value="ding">{t("settingsScreen.soundDing")}</Select.Option>
                  </Select>
                </div>
                <Button icon={<SoundOutlined />} style={{ width:"100%", borderRadius:8, borderColor:T.brand, color:T.brand, fontWeight:600, height:40 }}
                  onClick={() => {
                    try {
                      const a = new Audio(`${import.meta.env.BASE_URL}alert-preview.mp3`);
                      a.volume = Math.max(0, Math.min(1, volume / 100));
                      void a.play();
                    } catch { /* ignore autoplay blocks */ }
                  }}
                >
                  {t("settingsScreen.previewSound")}
                </Button>
              </SectionCard>

              <SectionCard icon={<TabletOutlined />} title={t("settingsScreen.channelsTitle")}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", paddingBottom:14, marginBottom:14, borderBottom:"1px solid #F3F4F6" }}>
                  <div>
                    <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{t("settingsScreen.tabletPushTitle")}</div>
                    <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.tabletPushDesc")}</div>
                  </div>
                  <Switch checked={tabletPushOn} onChange={setTabletPushOn} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", paddingBottom:14, marginBottom:14, borderBottom:"1px solid #F3F4F6" }}>
                  <div>
                    <div style={{ fontWeight:700, color:T.textDark, fontSize:14 }}>{t("settingsScreen.soundAlertChannelTitle")}</div>
                    <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.soundAlertChannelDesc")}</div>
                  </div>
                  <Switch checked={soundAlertOn} onChange={setSoundAlertOn} />
                </div>
                <div style={{ background:T.bg, borderRadius:8, padding:"10px 12px", fontSize:12, color:T.textMuted, display:"flex", gap:6 }}>
                  <InfoCircleOutlined style={{ flexShrink:0, marginTop:1 }} />
                  <span>{t("settingsScreen.smsEmailUnavailable")}</span>
                </div>
              </SectionCard>

              {/* Notification preview */}
              <div className="admin-settings-notification-preview" style={{ background:T.surface, borderRadius:12, border:"1px solid #E5E7EB", overflow:"hidden" }}>
                <div style={{ background:T.brand, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:11, fontWeight:800, color:T.surface, letterSpacing:0.5 }}>{t("settingsScreen.newOrderAlertBanner")}</span>
                  <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, background:"rgba(255,255,255,0.25)", color:T.surface }}>{t("settingsScreen.previewBadge")}</span>
                </div>
                <div style={{ padding:"12px 14px" }}>
                  <div style={{ fontWeight:700, color:T.textDark, fontSize:13, marginBottom:4 }}>{t("settingsScreen.previewOrderTitle")}</div>
                  <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.previewOrderItems")}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "sms" && (
        <div className="admin-settings-columns" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <SectionCard icon={<MessageOutlined />} title={t("settingsScreen.smsTemplatesTitle")}
              extra={<div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.smsTemplatesExtra")}</div>}
            >
              <div style={{ marginBottom:18 }}>
                {fieldLabel("lblSmsDefaultLang")}
                <Select value={smsDefaultLang} onChange={setSmsDefaultLang} style={{ width:"100%", marginTop:6, height:44 }}>
                  <Select.Option value="zh">{t("settingsScreen.langChinese")}</Select.Option>
                  <Select.Option value="en">{t("settingsScreen.langEnglish")}</Select.Option>
                  <Select.Option value="ms">{t("settingsScreen.langMalay")}</Select.Option>
                </Select>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:6 }}>{t("settingsScreen.smsDefaultLangHint")}</div>
              </div>

              {([
                ["zh", smsTemplateZh, setSmsTemplateZh, "langChinese"],
                ["en", smsTemplateEn, setSmsTemplateEn, "langEnglish"],
                ["ms", smsTemplateMs, setSmsTemplateMs, "langMalay"],
              ] as const).map(([lang, value, setValue, langKey]) => (
                <div key={lang} style={{ marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    {fieldLabel(langKey === "langChinese" ? "lblSmsTemplateZh" : langKey === "langEnglish" ? "lblSmsTemplateEn" : "lblSmsTemplateMs")}
                    {smsDefaultLang === lang && (
                      <span style={{ fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:20, background:T.brandLight, color:T.brand }}>
                        {t("settingsScreen.defaultBadge")}
                      </span>
                    )}
                  </div>
                  <Input.TextArea
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder={DEFAULT_SMS_TEMPLATES[lang]}
                  />
                </div>
              ))}

              <div style={{ background:T.bg, borderRadius:8, padding:"10px 12px", fontSize:12, color:T.textMuted, display:"flex", gap:6 }}>
                <InfoCircleOutlined style={{ flexShrink:0, marginTop:1 }} />
                <span>{t("settingsScreen.smsTemplateHint")}</span>
              </div>
            </SectionCard>

            <SectionCard icon={<InfoCircleOutlined />} title={t("settingsScreen.otpLimitsTitle")}
              extra={<div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.otpLimitsExtra")}</div>}
            >
              <div className="admin-settings-otp-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
                <div>
                  {fieldLabel("lblOtpDailyCap")}
                  <Input value={otpDailyCap} onChange={e => setOtpDailyCap(e.target.value.replace(/\D/g, ""))} style={{ ...inp, marginTop:6 }} />
                </div>
                <div>
                  {fieldLabel("lblOtpCooldownSeconds")}
                  <Input value={otpCooldownSeconds} onChange={e => setOtpCooldownSeconds(e.target.value.replace(/\D/g, ""))} style={{ ...inp, marginTop:6 }} />
                </div>
                <div>
                  {fieldLabel("lblOtpExpirySeconds")}
                  <Input value={otpExpirySeconds} onChange={e => setOtpExpirySeconds(e.target.value.replace(/\D/g, ""))} style={{ ...inp, marginTop:6 }} />
                </div>
                <div>
                  {fieldLabel("lblOtpCodeLength")}
                  <Input value={otpCodeLength} onChange={e => setOtpCodeLength(e.target.value.replace(/\D/g, ""))} style={{ ...inp, marginTop:6 }} />
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Right: live preview of what actually sends today */}
          <div style={{ width:290, flexShrink:0 }}>
            <SectionCard icon={<MobileOutlined />} title={t("settingsScreen.smsPreviewTitle")}>
              <div style={{ background:T.surfaceBlue, borderRadius:10, padding:"12px 14px", fontSize:13, color:T.textBody, lineHeight:1.6 }}>
                {renderSmsPreview(
                  (smsDefaultLang === "zh" ? smsTemplateZh : smsDefaultLang === "en" ? smsTemplateEn : smsTemplateMs) || DEFAULT_SMS_TEMPLATES[smsDefaultLang],
                )}
              </div>
              <div style={{ fontSize:11, color:T.textMuted, marginTop:10 }}>{t("settingsScreen.smsPreviewHint")}</div>
            </SectionCard>
          </div>
        </div>
      )}

      {activeTab === "registration" && (
        <div className="admin-settings-columns" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <SectionCard icon={<UserOutlined />} title={t("settingsScreen.registrationTitle")}
              extra={<div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.registrationExtra")}</div>}
            >
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0" }}>
                <div style={{ width:40, height:40, borderRadius:10, background:T.brandLight, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                  <MailOutlined />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:T.textDark, fontSize:14, marginBottom:4 }}>{t("settingsScreen.allowEmailRegistration")}</div>
                  <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.allowEmailRegistrationDesc")}</div>
                </div>
                <Switch checked={allowEmailRegistration} onChange={setAllowEmailRegistration} />
              </div>

              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0", borderTop:"1px solid #F3F4F6" }}>
                <div style={{ width:40, height:40, borderRadius:10, background:T.brandLight, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                  <PhoneOutlined />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:T.textDark, fontSize:14, marginBottom:4 }}>{t("settingsScreen.allowPhoneRegistration")}</div>
                  <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.allowPhoneRegistrationDesc")}</div>
                </div>
                <Switch checked={allowPhoneRegistration} onChange={setAllowPhoneRegistration} />
              </div>
            </SectionCard>
          </div>
        </div>
      )}

      {activeTab === "email" && (
        <div className="admin-settings-columns" style={{ display:"flex", gap:20, alignItems:"flex-start" }}>
          <div style={{ flex:1, minWidth:0 }}>
            <SectionCard icon={<MailOutlined />} title={t("settingsScreen.emailTitle")}
              extra={<div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.emailExtra")}</div>}
            >
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 0" }}>
                <div style={{ width:40, height:40, borderRadius:10, background:T.brandLight, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                  <MailOutlined />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, color:T.textDark, fontSize:14, marginBottom:4 }}>{t("settingsScreen.emailEnabled")}</div>
                  <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.emailEnabledDesc")}</div>
                </div>
                <Switch checked={emailEnabled} onChange={setEmailEnabled} />
              </div>

              <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                {fieldLabel("emailProvider")}
                <Select value={emailProvider} onChange={setEmailProvider} style={{ width:"100%", marginTop:6 }}
                  options={[
                    { value: "resend", label: "Resend" },
                    { value: "smtp", label: t("settingsScreen.emailProviderSmtp") },
                  ]} />
              </div>

              {emailProvider === "resend" ? (
                <>
                  <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    {fieldLabel("resendApiKey")}
                    <Input.Password value={resendApiKey} onChange={e => setResendApiKey(e.target.value)}
                      placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxx" style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    {fieldLabel("emailFromAddress")}
                    <Input value={emailFromAddress} onChange={e => setEmailFromAddress(e.target.value)}
                      placeholder="noreply@kawkaw.my" style={{ ...inp, marginTop:6 }} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    {fieldLabel("smtpHost")}
                    <Input value={smtpHost} onChange={e => setSmtpHost(e.target.value)}
                      placeholder="smtp.gmail.com" style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div className="admin-settings-smtp-grid" style={{ display:"flex", gap:16, padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    <div style={{ flex:1 }}>
                      {fieldLabel("smtpPort")}
                      <Input value={smtpPort} onChange={e => setSmtpPort(e.target.value)}
                        placeholder="587" style={{ ...inp, marginTop:6 }} />
                    </div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:20 }}>
                      <Switch checked={smtpSecure} onChange={setSmtpSecure} />
                      <span style={{ fontSize:13, color:T.textDark }}>{t("settingsScreen.smtpSecure")}</span>
                    </div>
                  </div>
                  <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    {fieldLabel("smtpUser")}
                    <Input value={smtpUser} onChange={e => setSmtpUser(e.target.value)}
                      placeholder="you@gmail.com" style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    {fieldLabel("smtpPass")}
                    <Input.Password value={smtpPass} onChange={e => setSmtpPass(e.target.value)}
                      placeholder={t("settingsScreen.smtpPassPlaceholder")} style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ padding:"16px 0", borderTop:"1px solid #F3F4F6" }}>
                    {fieldLabel("emailFromAddress")}
                    <Input value={emailFromAddress} onChange={e => setEmailFromAddress(e.target.value)}
                      placeholder="noreply@kawkaw.my" style={{ ...inp, marginTop:6 }} />
                  </div>
                </>
              )}
            </SectionCard>

            <SectionCard icon={<MailOutlined />} title={t("settingsScreen.emailTemplatesTitle")}
              extra={<div style={{ fontSize:13, color:T.textMuted }}>{t("settingsScreen.emailTemplatesExtra")}</div>}
            >
              <div style={{ marginBottom:18 }}>
                {fieldLabel("lblEmailTemplateDefaultLang")}
                <Select value={emailTemplateDefaultLang} onChange={setEmailTemplateDefaultLang} style={{ width:"100%", marginTop:6, height:44 }}>
                  <Select.Option value="zh">{t("settingsScreen.langChinese")}</Select.Option>
                  <Select.Option value="en">{t("settingsScreen.langEnglish")}</Select.Option>
                  <Select.Option value="ms">{t("settingsScreen.langMalay")}</Select.Option>
                </Select>
                <div style={{ fontSize:12, color:T.textMuted, marginTop:6 }}>{t("settingsScreen.emailTemplateHint")}</div>
              </div>

              {([
                ["zh", emailTemplateZh, setEmailTemplateZh, "langChinese"],
                ["en", emailTemplateEn, setEmailTemplateEn, "langEnglish"],
                ["ms", emailTemplateMs, setEmailTemplateMs, "langMalay"],
              ] as const).map(([lang, value, setValue, langKey]) => (
                <div key={lang} style={{ marginBottom:20, paddingBottom:20, borderBottom: lang !== "ms" ? "1px solid #F3F4F6" : undefined }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:T.textDark }}>{t(`settingsScreen.${langKey}` as const)}</span>
                    {emailTemplateDefaultLang === lang && (
                      <span style={{ fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:20, background:T.brandLight, color:T.brand }}>
                        {t("settingsScreen.defaultBadge")}
                      </span>
                    )}
                  </div>
                  <div style={{ marginBottom:10 }}>
                    {fieldLabel("lblEmailTemplateSubject")}
                    <Input value={value.subject} onChange={e => setValue({ ...value, subject: e.target.value })}
                      maxLength={200} style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ marginBottom:10 }}>
                    {fieldLabel("lblEmailTemplateHeading")}
                    <Input value={value.heading} onChange={e => setValue({ ...value, heading: e.target.value })}
                      maxLength={200} style={{ ...inp, marginTop:6 }} />
                  </div>
                  <div style={{ marginBottom:10 }}>
                    {fieldLabel("lblEmailTemplateIntro")}
                    <Input.TextArea value={value.intro} onChange={e => setValue({ ...value, intro: e.target.value })}
                      rows={2} maxLength={500} style={{ borderRadius:10, fontSize:14, resize:"none", marginTop:6 }} />
                  </div>
                  <div>
                    {fieldLabel("lblEmailTemplateOutro")}
                    <Input.TextArea value={value.outro} onChange={e => setValue({ ...value, outro: e.target.value })}
                      rows={2} maxLength={500} style={{ borderRadius:10, fontSize:14, resize:"none", marginTop:6 }} />
                  </div>
                </div>
              ))}
              <div style={{ fontSize:12, color:T.textMuted }}>{t("settingsScreen.emailTemplatePlaceholderHint")}</div>
            </SectionCard>
          </div>
        </div>
      )}
      </div>
        </div>
      </div>
    </AdminShell>
  );
}
